<template>
  <panel class="case-lab-media" :title="'Media' | translateFeatureLabel" color="green-4">
    <div
      v-for="labAsset in orderedLabAssets"
      :key="labAsset.id"
      class="lab-asset-container"
      :span="8"
    >
      <label>{{ labAsset.name[caseConf.language] | translateFeatureLabel }}</label>
      <el-form class="lab-asset-form">
        <el-upload
          v-if="labAsset.type === 'image' || labAsset.type === 'namedImage'"
          class="uploader"
          :class="{ disabled: readonly }"
          :disabled="readonly"
          list-type="picture-card"
          :file-list="getFilesFor(labAsset)"
          :before-upload="beforeUpload"
          :http-request="makeUploaderFor(labAsset)"
          :on-remove="!readonly ? makeOnRemove(labAsset) : null"
          :on-preview="preview"
          :multiple="false"
          accept="image/png, image/jpeg"
          action
        >
          <i class="el-icon-plus" />
        </el-upload>
        <div class="assets-infos-container">
          <div v-for="(file, index) in getFilesFor(labAsset)" :key="file.url" class="asset-infos">
            <toggle-button :value="!!file.enabled" :disabled="readonly" @toggle="value => toggleLabAssetImage(labAsset, file.url, value)">
              <md-tooltip md-direction="right" md-delay="300">
                Turning off media will make it unavailable at the start of the Simulation.  Re-enable media through a Case Logic effect.
              </md-tooltip>
            </toggle-button>
            <div class="asset-label">{{ file.name }}</div>
            <help-tooltip v-if="assetWarnings(labAsset, index)" type="warning">
              {{ (assetWarnings(labAsset, index).name || []).join(", ") }}
            </help-tooltip>
          </div>
        </div>
        <el-form-item v-if="labAsset.type === 'float'" :label="'Value'">
          <el-input
            :value="getLabAssetValue(labAsset)"
            :disabled="readonly"
            @input="value => setLabAssetValue(labAsset, value.length ? parseFloat(value) : undefined)"
          >
            <template slot="append">
              <el-button :disabled="readonly" icon="delete" @click="setLabAssetValue(labAsset, undefined)" />
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </div>
    <el-dialog
      v-fixed-position
      :visible="!!state.previewedFile"
      :before-close="closePreview"
      size="large"
    >
      <label v-if="state.previewedFile" class="previewed-file-name">{{ state.previewedFile.name }}</label>
      <img v-if="state.previewedFile" class="preview" :src="state.previewedFile.url" crossorigin="anonymous">
    </el-dialog>

    <md-dialog-confirm
      class="confirm-dialog"
      :md-active.sync="state.removeDialogOpen"
      md-title="Delete this image ?"
      :md-content="state.confirmationMessage"
      md-confirm-text="Delete"
      md-cancel-text="Cancel"
      @md-confirm="onConfirmRemove()"
    />
    <md-dialog-alert
      :md-active.sync="state.showDisallowedAlert"
      :md-content="'That file type isn\'t allowed: ' + state.disallowedFileName"
      md-confirm-text="OK"
    />
  </panel>
</template>

<script>
import {
  assign,
  debounce,
  pick,
  upperFirst,
  orderBy,
  findIndex,
  get,
  toArray,
  extend,
  forEach,
} from 'lodash';

import HelpTooltip from '~lib/common/components/HelpTooltip';

import ToggleButton from '../../common/ToggleButton';

import fixedPosition from '../../../directives/fixedPosition';
import servicesClient from '../../../providers/servicesClient';
import { create } from 'apisauce'
import Panel from '../../common/Panel';

const allowedImageTypes = [
  'image/jpeg',
  'image/png'
];

export default {
  name: 'case-lab-media',
  components: {
    Panel,
    ToggleButton,
    HelpTooltip,
  },
  directives: {
    fixedPosition,
  },
  props: {
    labAssets: {
      type: Array,
      default: () => [],
    },
    caseConf: {
      type: Object,
      default: () => ({
        language: 'en',
      }),
    },
    user: {
      type: Object,
      required: true,
    },
    warnings: {
      type: Array,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        caseConf: {},
        previewedFile: null,
        deletedMedia: {},
        removeDialogOpen: false,
        removeCandidate: null,
        confirmationMessage: '',
        disallowedFileName: null,
        showDisallowedAlert: false,
      },
    };
  },
  computed: {
    defaults() {
      return {
        _labAssets: [],
      };
    },
    availableLabAssets() {
      return this.labAssets.filter(
        (labAsset) =>
          !labAsset.productTypes ||
          labAsset.productTypes.includes(this.productType)
      );
    },
    orderedLabAssets() {
      return orderBy(this.availableLabAssets, ['type', 'name.en']);
    },
    productType() {
      return this.user['https://vrpatients.com/productType'];
    },
  },
  created() {
    this.state.caseConf = assign(
      {},
      this.defaults,
      pick(this.caseConf, '_labAssets'),
    );
  },
  mounted() {},
  methods: {
    beforeUpload(file) {
      if (!allowedImageTypes.includes(file.type)) {
        this.state.disallowedFileName = file.name;
        this.state.showDisallowedAlert = true;
        return false;
      }
      return true;
    },
    makeUploaderFor(labAsset) {
      // return async ({ file, onError, onProgress }) => {
      return async ({ file, onError }) => {
        try {
          const presignResponse = await servicesClient.post(`presigned-post?fileType=${file.type}`);
          const url = `${presignResponse.data.url}/${presignResponse.data.fields.key}`;

          const formData = new FormData();
          // formData.append("x-amz-meta-user-id", userId);
          formData.append('Content-Type', file.type);

          Object.entries(presignResponse.data.fields).forEach(([k, v]) => {
              formData.append(k, v);
          });

          formData.append('file', file);
          const api = create({
            baseURL: presignResponse.data.url
          });
          await api.post('', formData);
          const name = upperFirst(file.name.split('.')[0].replace(/[_]/g, ' '));
          this.addLabAssetImage(labAsset, url, name);
          return { url, labAsset };
        } catch (e) {
          onError(e);
        }
        return null;
      };
    },
    makeOnRemove(labAsset) {
      return file => {
        if (this.state.disallowedFileName && file.name.toLowerCase() === this.state.disallowedFileName.toLowerCase()) {
          return;
        }
        const assetUrl = (file.response && file.response.url) || file.url;

        this.state.removeCandidate = {
          labAsset,
          assetUrl,
        };

        if (this.isUsedInCaseLogic(assetUrl)) {
          this.state.confirmationMessage = `This Image is being used in Case
            Logic and if deleted will prevent your case from running properly.
            If you  delete, make sure to remove the associated case logic.`;
        } else {
          this.state.confirmationMessage = 'Are you sure you want to delete this image from the Case?';
        }

        this.forceUploadRefresh(labAsset);

        // Show confirmation dialog
        this.state.removeDialogOpen = true;
      };
    },
    forceUploadRefresh(labAsset) {
      // To force the Upload control to refresh its list, data within it must change
      const files = this.getFilesFor(labAsset);
      if (files.length > 0) {
        files[0].refresh += 1;
        this.$forceUpdate();
      }
    },
    onConfirmRemove() {
      if (this.state.removeCandidate) {
        this.removeLabAssetImage(
          this.state.removeCandidate.labAsset,
          this.state.removeCandidate.assetUrl,
        );
        this.state.removeCandidate = null;
      }
    },
    addLabAssetImage(labAsset, url, name) {
      const { _labAssets } = this.state.caseConf;
      const caseLabAsset = _labAssets.find(asset => asset.id === labAsset.id);
      const asset = {
        url,
        name: labAsset.type === 'namedImage' ? name : undefined,
        enabled: true,
      };
      if (!caseLabAsset) {
        _labAssets.push({
          id: labAsset.id,
          key: labAsset.key,
          options: {
            assets: [asset],
          },
        });
      } else {
        caseLabAsset.options.assets.push(asset);
      }
      this.restoreDeleted(url, name);

      this.emitUpdate();
      this.$analytics.event('Authoring Tool', 'media_added');
    },
    restoreDeleted(newUrl, newName) {
      // Update url of lab media effects if adding back a deleted media
      if (!this.state.deletedMedia) {
        return;
      }
      const nodes = toArray(get(this.caseConf, 'caseLogicDescription.nodes'));
      const labMediaEffectNodes = nodes.filter(
        (node) => node.name === 'Effect' && get(node, 'data.effect.type') === 'LAB_ASSET_AVAILABLE',
      );
      for (let i = 0; i < labMediaEffectNodes.length; i += 1) {
        const node = labMediaEffectNodes[i];
        if (this.state.deletedMedia.url === node.data.effect.url &&
            this.state.deletedMedia.name === newName) {
          node.data.effect.url = newUrl;
        }
      }
    },
    setLabAssetValue(labAsset, value) {
      const { _labAssets } = this.state.caseConf;
      const caseLabAsset = _labAssets.find(asset => asset.id === labAsset.id);
      if (!caseLabAsset) {
        _labAssets.push({
          id: labAsset.id,
          key: labAsset.key,
          options: {
            asset: { value },
          },
        });
      } else {
        caseLabAsset.options.asset.value = value;
      }
      this.emitUpdate();
    },
    getLabAssetValue(labAsset) {
      const { _labAssets } = this.state.caseConf;
      if (!_labAssets) {
        return undefined;
      }
      const caseLabAsset = _labAssets.find(asset => asset.id === labAsset.id);
      if (!caseLabAsset) {
        return undefined;
      }
      return caseLabAsset.options.asset.value;
    },
    removeLabAssetImage(labAsset, url) {
      const { _labAssets } = this.state.caseConf;
      const caseLabAsset = _labAssets.find(asset => asset.id === labAsset.id);

      // Remember most recently deleted asset
      this.state.deletedMedia = caseLabAsset.options.assets.find(
        asset => asset.url === url,
      );

      caseLabAsset.options.assets = caseLabAsset.options.assets.filter(
        asset => asset.url !== url,
      );
      this.emitUpdate();
    },
    toggleLabAssetImage(labAsset, url, value) {
      const { _labAssets } = this.state.caseConf;
      const caseLabAsset = _labAssets.find(asset => asset.id === labAsset.id);
      caseLabAsset.options.assets = caseLabAsset.options.assets.map(asset =>
        (asset.url === url ? { ...asset, enabled: value } : asset),
      );
      this.emitUpdate();
    },
    getFilesFor(labAsset) {
      const { _labAssets } = this.state.caseConf;
      if (!_labAssets) {
        return [];
      }
      const caseLabAsset = _labAssets.find(asset => asset.id === labAsset.id);
      if (!caseLabAsset) {
        return [];
      }
      // add dummy field to use to trigger refresh
      return caseLabAsset.options.assets.map(asset => extend({}, asset, { refresh: 0 }));
    },
    preview(file) {
      this.state.previewedFile = file;
    },
    closePreview() {
      this.state.previewedFile = null;
    },
    labAssetWarnings(labAsset) {
      const indexInCaseConf = findIndex(this.caseConf._labAssets, { id: labAsset.id });
      return this.warnings && this.warnings[indexInCaseConf].error;
    },
    assetWarnings(labAsset, assetIndex) {
      const labAssetWarnings = this.labAssetWarnings(labAsset);
      if (!labAssetWarnings) {
        return null;
      }
      const assetsWarnings = labAssetWarnings['options.assets'];
      return assetsWarnings && assetsWarnings[assetIndex].error;
    },
    emitUpdate: debounce(function emitUpdate() {
      this.$emit('update', this.state.caseConf);
    }),
    get,
    isUsedInCaseLogic(url) {
      let isUsed = false;

      const nodes = toArray(get(this.caseConf, 'caseLogicDescription.nodes'));

      const checkNodes = nodes.filter((node) => node.name === 'Check');
      checkNodes.forEach((node) => {
        forEach(node.data, (check) => {
          if (check.type === 'LAB_ASSET_VIEWED' && url === check.url) {
            isUsed = true;
          }
        });
      });

      const mediaEffectNodes = nodes.filter(
        (node) => node.name === 'Effect' && get(node, 'data.effect.type') === 'LAB_ASSET_AVAILABLE',
      );
      mediaEffectNodes.forEach(mediaEffectNode => {
        const mediaUrl = get(mediaEffectNode, 'data.effect.url');
        if (url === mediaUrl) {
          isUsed = true;
        }
      });
      return isUsed;
    },

  },
};
</script>

<style scoped>
.lab-asset-container {
  padding-top: 15px;
}

.preview {
  max-width: 100%;
  display: block;
  margin: auto;
}

.previewed-file-name {
  text-align: center;
}

.lab-asset-form {
  position: relative;
}

.uploader {
  position: relative;
}

.uploader .el-icon-plus {
  color: white;
}

.assets-infos-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  position: absolute;
  top: 0;
  left: 0;
}

.asset-infos {
  width: 148px;
  height: 194px;
  padding-top: 152px;
  position: relative;
  margin-right: 8px;
  font-weight: 500;
}

.asset-infos {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.asset-infos .toggle-button {
  margin-right: 5px;
}

.asset-infos .asset-label {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-height: 20px; /* fallback */
  max-height: 40px; /* fallback */
}

.asset-infos .help-tooltip {
  margin-left: 5px;
  min-width: 22px;
}
</style>

<style>
.case-lab-assets .uploader.disabled > div:last-child {
  display: none;
}
.uploader > .el-upload {
  background-color: #40883f;
}

.case-lab-media .uploader .el-upload-list__item {
  margin-bottom: 40px;
  z-index: 10;
}

.case-lab-media .uploader .el-upload {
  position: relative;
  z-index: 10;
}
</style>
